import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { CustomUIRenderer, ModalCustomUIRenderer } from './CustomUIRenderer';
import { UIKitRenderer } from './UIKitRenderer';

import {
	makeFrame,
	makeModalThreeLOPromptForCustomUI,
	makeThreeLOPromptForCustomUI,
} from '../../components';
import { ForgeUIExtensionAnalyticsContext } from '../../analytics';
import {
	type ForgeUIRendererProps,
	type ConfluenceCustomUIProps,
	type CloudOrWorkspaceId,
} from './types';
import { isCustomUI } from '../../utils/isCustomUI';
import { useResizeAnalytics } from '../../utils/analytics/useResizeAnalytics';
import { fg } from '@atlaskit/platform-feature-flags';
import { type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { Box, xcss } from '@atlaskit/primitives';

type Props = ForgeUIRendererProps & CloudOrWorkspaceId & ConfluenceCustomUIProps;

const wrapperStyle = xcss({
	height: '100%',
});

const HeightAnalyticsWrapper = ({
	children,
	extension,
	entryPoint,
}: {
	children: ReactNode;
	extension: ForgeUIExtensionType;
	entryPoint?: string;
}) => {
	const { containerRef } = useResizeAnalytics({
		isEnabled: extension.type === 'xen:macro' && fg('forge-ui-macro-resize-event'),
		trackedAttributeValues: {
			appType: isCustomUI(extension, entryPoint) ? 'CustomUI' : 'UIKit',
			extensionType: extension.type,
			forgeEnvironment: extension.environmentType,
		},
	});

	if (extension.type === 'xen:macro' && fg('forge-ui-macro-resize-event')) {
		return (
			<Box xcss={wrapperStyle} ref={containerRef} id="forge-ui-height-observer-wrapper">
				{children}
			</Box>
		);
	}
	return <>{children}</>;
};

export const ForgeUIRenderer = (props: Props) => {
	const {
		accountId,
		bridge,
		client,
		components,
		consentMessage,
		contextIds,
		customBridgeMethods,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		hasModalConsentPrompt,
		height,
		width,
		isIframeHidden,
		isIframeResizable,
		getContextToken,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onConsentInitialization,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		onInitialRender,
		product,
		timezone,
		getModalWidth,
		onModalExtensionClose,
		onSuccess,
		onAppError,
	} = props;

	let cloudId: string | undefined;
	let workspaceId: string | undefined;

	if ('cloudId' in props) {
		cloudId = props.cloudId;
	} else if ('workspaceId' in props) {
		workspaceId = props.workspaceId;
	}

	const cloudOrWorkspaceId = cloudId ? { cloudId } : ({ workspaceId } as { workspaceId: string });

	useEffect(() => {
		onInitialRender?.();
	}, [onInitialRender]);

	const { id: extensionId, environmentId, environmentType } = extension;
	const coreData = useMemo(
		() => ({
			workspaceId,
			cloudId,
			localId: localId || extensionId,
			environmentId,
			environmentType,
		}),
		[cloudId, environmentId, environmentType, extensionId, localId, workspaceId],
	);

	const getThreeLOPrompt = useCallback(
		() => ({
			ThreeLOPrompt:
				modalExtension || hasModalConsentPrompt
					? makeModalThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							isBlanketHidden: true,
							onClose: onConsentModalClose,
						})
					: makeThreeLOPromptForCustomUI({
							appName: extension.properties.title,
							onSuccess: onConsentSuccess,
							message: consentMessage,
							onInitialization: onConsentInitialization,
						}),
		}),
		[
			extension.properties.title,
			consentMessage,
			hasModalConsentPrompt,
			modalExtension,
			onConsentSuccess,
			onConsentModalClose,
			onConsentInitialization,
		],
	);

	const customUIProps = {
		accountId,
		bridge,
		client,
		consentMessage,
		contextIds,
		coreData,
		customBridgeMethods,
		dialogs,
		entryPoint,
		extension,
		extensionData,
		extensionPayload,
		extensionViewData,
		getContextToken,
		getThreeLOPrompt,
		height,
		loadingComponent,
		locale,
		localId,
		mentionProvider,
		modalExtension,
		modalStyles,
		modalProvider,
		onConsentModalClose,
		onConsentSuccess,
		onError,
		onForgeDocUpdated,
		onIframeLoad,
		product,
		timezone,
		isIframeHidden,
		isIframeResizable,
	};

	const [viewOnClose, setViewOnClose] = useState<() => Promise<void>>();

	return (
		<HeightAnalyticsWrapper extension={extension} entryPoint={entryPoint}>
			<ForgeUIExtensionAnalyticsContext
				extensionId={extension.id}
				localId={localId || extension.id}
			>
				{/* hidden span used for testing purposes and to identify that this renderer is being used when toggling the feature flag in products*/}
				<span hidden id="forge-ui-renderer" />
				{!isCustomUI(extension, entryPoint) ? (
					<UIKitRenderer
						coreData={coreData}
						getThreeLOPrompt={getThreeLOPrompt}
						accountId={accountId}
						client={client}
						contextIds={contextIds}
						extension={extension}
						extensionData={extensionData}
						extensionViewData={extensionViewData}
						product={product}
						bridge={bridge}
						customBridgeMethods={customBridgeMethods}
						components={{
							Frame: makeFrame((inputProps) => {
								return (
									<CustomUIRenderer
										{...customUIProps}
										extension={{
											...extension,
											properties: { ...extension.properties, ...inputProps },
										}}
										// Ensure extensions that are rendered as entryPoints aren't passing through the very same entryPoint to the Frame
										entryPoint={undefined}
										// Ensures Frame is always resized to the full height of the content
										// Required because Jira leaves isIframeResizable as false
										isIframeResizable={true}
									/>
								);
							}),
							...components,
						}}
						consentMessage={consentMessage}
						entryPoint={entryPoint}
						getContextToken={getContextToken}
						locale={locale}
						localId={localId}
						mentionProvider={mentionProvider}
						modalExtension={modalExtension}
						onConsentSuccess={onConsentSuccess}
						onForgeDocUpdated={onForgeDocUpdated}
						timezone={timezone}
						onConsentModalClose={onConsentModalClose}
						onError={onError}
						loadingComponent={loadingComponent}
						extensionPayload={extensionPayload}
						onSuccess={onSuccess}
						{...cloudOrWorkspaceId}
						onAppError={onAppError}
					/>
				) : modalExtension ? (
					// getModalWidth not applied here since all modal extensions are defaulted to medium width
					<ModalCustomUIRenderer
						{...customUIProps}
						bridge={{
							onClose: (callback) => {
								setViewOnClose(() => callback.data);
								// stores the callback for when modal is closed
								return true;
							},
							// spread down here so product can override the default onClose
							...customUIProps.bridge,
						}}
						modalOnClose={onModalExtensionClose}
						closeOnEscape={modalExtension?.shouldCloseOnEscapePress ?? true}
						closeOnOverlayClick={modalExtension?.shouldCloseOnOverlayClick ?? true}
						viewOnClose={viewOnClose}
					/>
				) : (
					<CustomUIRenderer {...customUIProps} getModalWidth={getModalWidth} width={width} />
				)}
			</ForgeUIExtensionAnalyticsContext>
		</HeightAnalyticsWrapper>
	);
};
